import { DateTime } from 'luxon';
import React, { ComponentProps, ReactNode } from 'react';
import { formatTimezoneAndDateTime } from 'services/UtilityService';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import CalendarIcon from 'img/icons/calendar.svg';
import ChecklistIcon from 'img/icons/checklist.svg';
import RequestedIcon from 'img/icons/requested-basic.svg';
import SVG from 'react-inlinesvg';
import ScheduledIcon from 'img/icons/scheduled-calendar-icon.svg';
import { v4 as uuidv4 } from 'uuid';
import { ExamStatus } from 'types/exam';
import FormattedDateTime from 'components/FormattedDateTime';
import { TFunction, Trans } from 'react-i18next';
import { isNumber } from 'lodash';
import { PremiumSessionUserFacingStatus } from 'types/premiumSession';
import { CheckIcon, TimesIcon } from '@patternfly/react-icons';
import classNames from 'classnames';
import usePremiumSessionAggregateInfo from 'hooks/usePremiumSessionAggregateInfo';
import { Flex } from '@patternfly/react-core';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import {
  BodyRowProps,
  BodyRowType,
  ExamStatusInfo,
  LiveVirtualClassInfo,
} from '../utils/types';

const SLICED_ROWS_LAST_ITEM = 1;

enum SessionCourseSections {
  Chapter = 'ch',
  Preface = 'pr',
}

const createCustomRow = (content?: ReactNode) => ({
  content,
  key: uuidv4(),
  type: BodyRowType.Custom,
});

/*
 * Returns the label for the exam status.
 */
function getExamCardStatusLabel(
  status: ExamStatus,
  t: TFunction<'translation', undefined>,
): string {
  switch (status) {
    case ExamStatus.Requested:
      return t('Requested');

    case ExamStatus.Unscheduled:
      return t('Enrolled');
    case ExamStatus.Scheduled:
      return t('Scheduled');
    case ExamStatus.ResultsPending:
      return t('Results pending');
    case ExamStatus.Completed:
      return t('Completed');

    case ExamStatus.RetakeAvailable:
      return t('Retake available');
    case ExamStatus.RetakeScheduled:
      return t('Retake scheduled');
    case ExamStatus.RetakeResultsPending:
      return t('Results pending');
    case ExamStatus.RetakeComplete:
      return t('Retake Completed');
    default:
      return '';
  }
}

export const formatProductRows = (offeringProducts: string[]) => {
  const formatedRows: ComponentProps<typeof OfferingCard.BodyRow>[] =
    offeringProducts.map((product) => createCustomRow(product));

  if (formatedRows.length > 2) {
    const slicedRows = formatedRows.slice(0, 2);

    slicedRows[SLICED_ROWS_LAST_ITEM] = {
      ...slicedRows[SLICED_ROWS_LAST_ITEM],
      customLabel: `+${formatedRows.length - 2}`,
    };

    return slicedRows;
  }

  return formatedRows;
};

export const formatExamStatusRows = (
  status: ExamStatusInfo,
  t: TFunction<'translation', undefined>,
) => {
  const examStatusLabel = getExamCardStatusLabel(status.status, t);
  const titleRow = createCustomRow(<b>{examStatusLabel}</b>);

  if (status?.content) {
    const descriptionRow = createCustomRow();

    if (status.status === ExamStatus.Unscheduled)
      descriptionRow.content = (
        <span style={{ color: 'red' }}>{status.content}</span>
      );

    if (status.status === ExamStatus.Scheduled)
      descriptionRow.content = (
        <FormattedDateTime>{status.content}</FormattedDateTime>
      );

    return [titleRow, descriptionRow];
  }

  return [titleRow];
};

export const formatExamExperienceQuotaRows = (quotaInfo: {
  allowed: number;
  used: number;
}) => {
  const { allowed, used } = quotaInfo;
  if (!isNumber(allowed) || !isNumber(used)) {
    // should never happen
    return [];
  }
  const quotaRow = createCustomRow(
    <Trans values={{ allowed, used }}>
      <strong>Used:</strong> {{ used }}/{{ allowed }}
    </Trans>,
  );
  return [quotaRow];
};

export const formatPremiumSessionRows = (
  premiumSessionStatus: PremiumSessionUserFacingStatus,
  attendedSessionDate: string,
  schedulledSessionDate: string,
  t: TFunction<'translation', undefined>,
) => {
  if (!premiumSessionStatus) return [];

  const sessionStatusLabels = {
    [PremiumSessionUserFacingStatus.Attended]: t('Attended'),
    [PremiumSessionUserFacingStatus.Enrolled]: t('Scheduled'),
    [PremiumSessionUserFacingStatus.Requested]: t('Requested'),
    [PremiumSessionUserFacingStatus.Schedulable]: t('Available'),
    [PremiumSessionUserFacingStatus.Unavailable]: t('Unavailable'),
  };

  const titleRow = createCustomRow(
    <b>{sessionStatusLabels[premiumSessionStatus]}</b>,
  );

  const descriptionRow = createCustomRow();

  switch (premiumSessionStatus) {
    case PremiumSessionUserFacingStatus.Attended:
      descriptionRow.content = t('{{date}}', {
        date: DateTime.fromISO(attendedSessionDate).toFormat(
          'ccc, MMM dd yyyy, hh:mm a',
        ),
      });
      return [titleRow, descriptionRow];
    case PremiumSessionUserFacingStatus.Enrolled:
      descriptionRow.content = t('{{date}}', {
        date: DateTime.fromISO(schedulledSessionDate).toFormat(
          'ccc, MMM dd yyyy, hh:mm a',
        ),
      });
      return [titleRow, descriptionRow];
    case PremiumSessionUserFacingStatus.Unavailable:
      descriptionRow.content = t('Request is available');
      return [titleRow, descriptionRow];
    default:
      break;
  }

  return [titleRow];
};

export const getPremiumSessionTitle = (
  sessionModulesContent: {
    [key: string]: number[];
  },
  t: TFunction<'translation', undefined>,
) => {
  const chapterArray = sessionModulesContent[SessionCourseSections.Chapter];
  const prefaceArray = sessionModulesContent[SessionCourseSections.Preface];

  if (!chapterArray?.length && !prefaceArray?.length) return '';

  const prefaceTitle = t('Preface 1');
  let chapterTitle: string;

  if (chapterArray?.length === 1) {
    const chapterNumber = chapterArray[0];
    chapterTitle = t('Chapter {{chapterNumber}}', { chapterNumber });
  }

  if (chapterArray?.length > 1) {
    const firstNumbersSeparatedByComma = chapterArray.slice(0, -1).join(', ');
    const lastNumber = chapterArray[chapterArray?.length - 1];
    chapterTitle = t(
      'Chapter {{firstNumbersSeparatedByComma}} and {{lastNumber}}',
      {
        firstNumbersSeparatedByComma,
        lastNumber,
      },
    );
  }

  if (prefaceArray?.length && chapterArray?.length) {
    return (
      <>
        {prefaceTitle}
        <br />
        {chapterTitle}
      </>
    );
  }

  return chapterTitle;
};

export const getSessionsCustomIconRowConfig = (
  sessionStatusList: ReturnType<
    typeof usePremiumSessionAggregateInfo
  >['sessionsStatusList'],
) => {
  const formattedBodyRows = sessionStatusList.map(({ slug, status }) => {
    let icon: React.ReactNode;

    switch (status) {
      case PremiumSessionUserFacingStatus.Attended:
        icon = <CheckIcon />;
        break;
      case PremiumSessionUserFacingStatus.Enrolled:
        icon = <SVG src={ScheduledIcon} />;
        break;
      case PremiumSessionUserFacingStatus.Requested:
        icon = <SVG src={RequestedIcon} />;
        break;
      case PremiumSessionUserFacingStatus.Unavailable:
        icon = <TimesIcon color="#ee0000" />;
        break;

      default:
        icon = (
          <div className="session-number">
            <span>{slug.match(/-s0?(\d+)$/)?.[1] ?? '0'}</span>
          </div>
        );
        break;
    }

    return (
      <div
        className={classNames('session-body-item', {
          unavailable: status === PremiumSessionUserFacingStatus.Unavailable,
          attended: status === PremiumSessionUserFacingStatus.Attended,
          scheduled: status === PremiumSessionUserFacingStatus.Enrolled,
          requested: status === PremiumSessionUserFacingStatus.Requested,
        })}
      >
        {icon}
      </div>
    );
  });

  const [firstBodyRow, secondBodyRow] = [
    formattedBodyRows.slice(0, 5),
    formattedBodyRows.slice(5, 10),
  ];

  const formatedFirstBodyRow = createCustomRow(
    <Flex className="session-row-container" gap={{ default: 'gapXs' }}>
      {firstBodyRow}
    </Flex>,
  );

  if (!secondBodyRow.length) return [formatedFirstBodyRow];

  const formatedSecondBodyRow = createCustomRow(
    <Flex className="session-row-container" gap={{ default: 'gapXs' }}>
      {secondBodyRow}
    </Flex>,
  );

  return [formatedFirstBodyRow, formatedSecondBodyRow];
};

export const getLiveVirtualClassRows = (
  lvcStatus: CourseLvcUserFacingStatus,
  liveVirtualClassInfo: LiveVirtualClassInfo,
  t: TFunction<'translation', undefined>,
  timezone?: string,
) => {
  const {
    attentedEnrolledLVC,
    lvcAttended,
    lvcQuota,
    ongoingEnrolledLVC,
    upcomingEnrolledLVC,
  } = liveVirtualClassInfo;

  if (lvcStatus === CourseLvcUserFacingStatus.Enrolled) {
    const { end_time: endTime, start_time: startTime } =
      upcomingEnrolledLVC?.[0];

    const formatedStartDate = formatTimezoneAndDateTime(startTime, timezone);
    const formatedEndDate = formatTimezoneAndDateTime(endTime, timezone);

    return [
      createCustomRow(<b>{t('Scheduled')}</b>),
      createCustomRow(
        <Trans values={{ formatedStartDate }}>
          <strong>{t('Start:')}</strong> {{ formatedStartDate }}
        </Trans>,
      ),
      createCustomRow(
        <Trans values={{ formatedEndDate }}>
          <strong>{t('End:')}</strong> {{ formatedEndDate }}
        </Trans>,
      ),
    ];
  }

  if (lvcStatus === CourseLvcUserFacingStatus.Ongoing) {
    const {
      duration_days: durationDays,
      end_time: endTime,
      start_time: startTime,
    } = ongoingEnrolledLVC?.[0];

    const formatedEndDate = formatTimezoneAndDateTime(endTime, timezone);
    const currentDay =
      Math.floor(
        DateTime.now().diff(DateTime.fromISO(startTime), 'days').days,
      ) + 1;

    return [
      createCustomRow(
        <Trans values={{ currentDay, durationDays }}>
          <strong>Live class in progress:</strong> {{ currentDay }}/
          {{ durationDays }} days
        </Trans>,
      ),
      createCustomRow(
        <Trans values={{ formatedEndDate }}>
          <strong>End:</strong> {{ formatedEndDate }}
        </Trans>,
      ),
    ];
  }

  if (lvcStatus === CourseLvcUserFacingStatus.Attended) {
    const { end_time: endTime, start_time: startTime } =
      attentedEnrolledLVC?.[0];

    const formatedStartDate = DateTime.fromISO(startTime).toFormat('MMM dd');
    const formatedEndDate = DateTime.fromISO(endTime).toFormat('MMM dd, yyyy');

    return [
      createCustomRow(
        <Trans values={{ lvcAttended, lvcQuota }}>
          <strong>{t('Attended:')}</strong> {{ lvcAttended }} of {{ lvcQuota }}
        </Trans>,
      ),
      createCustomRow(
        <Trans values={{ formatedStartDate, formatedEndDate }}>
          Date: {{ formatedStartDate }} - {{ formatedEndDate }}
        </Trans>,
      ),
    ];
  }

  return [];
};

export const getVirtualTrainingRows = (
  t: TFunction<'translation', undefined>,
  startTime: string,
  timezone?: string,
) => {
  const formatedStartDate = formatTimezoneAndDateTime(startTime, timezone);

  return [
    createCustomRow(
      <Trans>
        <strong>{t('Starts')}</strong>
      </Trans>,
    ),
    createCustomRow(
      <Trans values={{ formatedStartDate }}>{{ formatedStartDate }}</Trans>,
    ),
  ];
};

export const getSkillsAssessmentRows = (
  timestamp: string,
  totalScore: number,
  t: TFunction<'translation', undefined>,
): BodyRowProps[] => {
  const formattedDate = DateTime.fromISO(timestamp).toFormat('MMM dd, yyyy');

  return [
    {
      icons: [{ path: CalendarIcon, label: formattedDate }],
      type: BodyRowType.Icons,
    },
    {
      icons: [
        {
          path: ChecklistIcon,
          label: `${t('Overall score')}: ${totalScore}%`,
        },
      ],
      type: BodyRowType.Icons,
    },
  ];
};
